import React, { useState, useEffect } from 'react';
import { Plus, Search, CreditCard as Edit, Trash2, Users, Shield, CheckCircle, XCircle } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'account_manager' | 'content_creator' | 'designer' | 'quality_reviewer' | 'client_viewer';
  permissions: string[];
  clients_assigned: string[];
  avatar_url?: string;
  is_active: boolean;
  last_login: string;
  created_at: string;
}

export default function TeamManagement() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'content_creator' as TeamMember['role'],
    clients_assigned: [] as string[]
  });

  const roleDefinitions = {
    admin: {
      name: 'Administrator',
      description: 'Full system access and team management',
      permissions: ['manage_team', 'manage_clients', 'manage_campaigns', 'create_posts', 'edit_posts', 'delete_posts', 'approve_posts', 'reject_posts', 'schedule_posts', 'publish_posts', 'view_analytics'],
      color: 'bg-red-100 text-red-800'
    },
    account_manager: {
      name: 'Account Manager',
      description: 'Client relationship and campaign management',
      permissions: ['manage_clients', 'manage_campaigns', 'create_posts', 'edit_posts', 'schedule_posts', 'view_analytics'],
      color: 'bg-blue-100 text-blue-800'
    },
    content_creator: {
      name: 'Content Creator',
      description: 'Create and manage social media content',
      permissions: ['create_posts', 'edit_posts', 'schedule_posts'],
      color: 'bg-green-100 text-green-800'
    },
    designer: {
      name: 'Designer',
      description: 'Create visual content and designs',
      permissions: ['create_posts', 'edit_posts'],
      color: 'bg-purple-100 text-purple-800'
    },
    quality_reviewer: {
      name: 'Quality Reviewer',
      description: 'Review and approve content before publishing',
      permissions: ['approve_posts', 'reject_posts', 'view_analytics'],
      color: 'bg-yellow-100 text-yellow-800'
    },
    client_viewer: {
      name: 'Client Viewer',
      description: 'View-only access for client representatives',
      permissions: ['view_analytics'],
      color: 'bg-gray-100 text-gray-800'
    }
  };

  const clients = [
    { id: '1', name: 'TechCorp Solutions' },
    { id: '2', name: 'Fashion Forward' },
    { id: '3', name: 'Healthy Eats' },
    { id: '4', name: 'Fitness Studio' },
    { id: '5', name: 'Restaurant Chain' }
  ];

  useEffect(() => {
    // Mock data
    setTeamMembers([
      {
        id: '1',
        name: 'John Doe',
        email: 'john@cyberedge.com',
        role: 'admin',
        permissions: roleDefinitions.admin.permissions,
        clients_assigned: [],
        avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
        is_active: true,
        last_login: '2024-03-10T14:30:00Z',
        created_at: '2024-01-15T09:00:00Z'
      },
      {
        id: '2',
        name: 'Sarah Wilson',
        email: 'sarah@cyberedge.com',
        role: 'designer',
        permissions: roleDefinitions.designer.permissions,
        clients_assigned: ['1', '2'],
        avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
        is_active: true,
        last_login: '2024-03-10T16:45:00Z',
        created_at: '2024-02-01T10:30:00Z'
      },
      {
        id: '3',
        name: 'Mike Johnson',
        email: 'mike@cyberedge.com',
        role: 'quality_reviewer',
        permissions: roleDefinitions.quality_reviewer.permissions,
        clients_assigned: ['1', '2', '3'],
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
        is_active: true,
        last_login: '2024-03-10T12:15:00Z',
        created_at: '2024-02-10T14:00:00Z'
      },
      {
        id: '4',
        name: 'Lisa Davis',
        email: 'lisa@cyberedge.com',
        role: 'content_creator',
        permissions: roleDefinitions.content_creator.permissions,
        clients_assigned: ['3', '4', '5'],
        avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
        is_active: true,
        last_login: '2024-03-10T11:20:00Z',
        created_at: '2024-02-15T16:30:00Z'
      },
      {
        id: '5',
        name: 'Alex Chen',
        email: 'alex@cyberedge.com',
        role: 'account_manager',
        permissions: roleDefinitions.account_manager.permissions,
        clients_assigned: ['1', '4'],
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
        is_active: false,
        last_login: '2024-03-08T09:45:00Z',
        created_at: '2024-01-20T11:15:00Z'
      }
    ]);
  }, []);

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    const member: TeamMember = {
      id: Date.now().toString(),
      ...newMember,
      permissions: roleDefinitions[newMember.role].permissions,
      avatar_url: undefined,
      is_active: true,
      last_login: new Date().toISOString(),
      created_at: new Date().toISOString()
    };
    
    setTeamMembers(prev => [...prev, member]);
    setNewMember({
      name: '',
      email: '',
      role: 'content_creator',
      clients_assigned: []
    });
    setShowAddModal(false);
  };

  const toggleMemberStatus = (id: string) => {
    setTeamMembers(prev => prev.map(member => 
      member.id === id ? { ...member, is_active: !member.is_active } : member
    ));
  };

  const deleteMember = (id: string) => {
    if (confirm('Are you sure you want to remove this team member?')) {
      setTeamMembers(prev => prev.filter(member => member.id !== id));
    }
  };

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600">Manage team members, roles, and permissions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Team Member
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search team members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Roles</option>
          {Object.entries(roleDefinitions).map(([key, role]) => (
            <option key={key} value={key}>{role.name}</option>
          ))}
        </select>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="divide-y divide-gray-200">
          {filteredMembers.map((member) => (
            <div key={member.id} className="p-6 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-500 font-medium">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    )}
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                      member.is_active ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900">{member.name}</h3>
                    <p className="text-gray-600">{member.email}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${roleDefinitions[member.role].color}`}>
                        {roleDefinitions[member.role].name}
                      </span>
                      {member.clients_assigned.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {member.clients_assigned.length} clients assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="text-right text-sm text-gray-500 mr-4">
                    <p>Last login:</p>
                    <p>{new Date(member.last_login).toLocaleDateString()}</p>
                  </div>
                  
                  <button
                    onClick={() => toggleMemberStatus(member.id)}
                    className={`p-2 rounded-lg ${
                      member.is_active 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    {member.is_active ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                  </button>
                  
                  <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Edit className="h-5 w-5" />
                  </button>
                  
                  <button 
                    onClick={() => deleteMember(member.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Permissions */}
              <div className="mt-4 pl-16">
                <div className="flex flex-wrap gap-2">
                  {member.permissions.slice(0, 5).map((permission) => (
                    <span key={permission} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {permission.replace('_', ' ')}
                    </span>
                  ))}
                  {member.permissions.length > 5 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      +{member.permissions.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Team Member</h2>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      required
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value as TeamMember['role'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(roleDefinitions).map(([key, role]) => (
                      <option key={key} value={key}>{role.name}</option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    {roleDefinitions[newMember.role].description}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assign Clients</label>
                  <div className="grid grid-cols-2 gap-2">
                    {clients.map((client) => (
                      <label key={client.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newMember.clients_assigned.includes(client.id)}
                          onChange={(e) => {
                            const updatedClients = e.target.checked
                              ? [...newMember.clients_assigned, client.id]
                              : newMember.clients_assigned.filter(id => id !== client.id);
                            setNewMember({ ...newMember, clients_assigned: updatedClients });
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{client.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {roleDefinitions[newMember.role].permissions.map((permission) => (
                        <span key={permission} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {permission.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}